import { useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap"
import { showError, showSuccess } from "../../redux/counter/counterSlice"
import { useAppDispatch } from "../../redux/store"
import {
    useVrboCreateCallbackConfigMutation,
    useVrboDeleteCallbackConfigMutation,
    useVrboGetNotificationEventTypesQuery,
    useVrboGetNotificationProfileQuery,
    useVrboRefreshCallbackConfigSecretMutation,
    useVrboRefreshNotificationProfileMutation,
    useVrboSendTestNotificationMutation,
    useVrboSubscribeEventTypeMutation,
    useVrboUnsubscribeEventTypeMutation,
    useVrboUpdateCallbackConfigMutation,
} from "../../services/bClientApi"
import timeService from "../../services/time/timeService"
import JSONData from "../JSONData/JSONData"
import LoadingSpinner from "../Loader/LoadingSpinner"
import { ConfirmModal } from "../Modals/confirmModal"

const DATE_TIME_FORMAT = 'LLL dd yyyy, h:mm a'
const DEFAULT_TIMEOUT_SECONDS = 10

const formatDateTime = (value?: string | null) => value ? timeService.getFormattedTime(value, DATE_TIME_FORMAT) : '-'

type CallbackConfigType = {
    id: string
    callbackUrl: string
    contactEmail: string
    previousSecretExpirationDateTime?: string | null
    secretExpirationDateTime?: string | null
    requestTimeoutSeconds?: number | null
}

export default function VrboWebhooksManager() {

    const dispatch = useAppDispatch()

    const { data: profileData, isLoading: isLoadingProfile, isFetching: isFetchingProfile, error: profileError } = useVrboGetNotificationProfileQuery()
    const { data: eventTypesData, isLoading: isLoadingEventTypes, error: eventTypesError } = useVrboGetNotificationEventTypesQuery()

    const [refreshProfile, { isLoading: isRefreshingProfile }] = useVrboRefreshNotificationProfileMutation()
    const [createCallbackConfig, { isLoading: isCreating }] = useVrboCreateCallbackConfigMutation()
    const [updateCallbackConfig, { isLoading: isUpdating }] = useVrboUpdateCallbackConfigMutation()
    const [deleteCallbackConfig, { isLoading: isDeleting }] = useVrboDeleteCallbackConfigMutation()
    const [refreshSecret, { isLoading: isRefreshingSecret }] = useVrboRefreshCallbackConfigSecretMutation()
    const [subscribeEventType, { isLoading: isSubscribing }] = useVrboSubscribeEventTypeMutation()
    const [unsubscribeEventType, { isLoading: isUnsubscribing }] = useVrboUnsubscribeEventTypeMutation()
    const [sendTestNotification, { isLoading: isSendingTest }] = useVrboSendTestNotificationMutation()

    const [newTimeout, setNewTimeout] = useState<number>(DEFAULT_TIMEOUT_SECONDS)
    const [editingConfigId, setEditingConfigId] = useState<string | null>(null)
    const [editForm, setEditForm] = useState<any>({})
    const [keepExistingSecretActive, setKeepExistingSecretActive] = useState(false)
    const [deleteModalConfigId, setDeleteModalConfigId] = useState<string | null>(null)
    const [secretModalConfigId, setSecretModalConfigId] = useState<string | null>(null)

    const [testEventType, setTestEventType] = useState('')
    const [testPayload, setTestPayload] = useState('{}')
    const [testResult, setTestResult] = useState<any>(null)

    const profile = profileData?.response?.notificationProfile
    const callbackConfigs: CallbackConfigType[] = profile?.callbackConfigs || []
    const eventTypes: { name: string, description: string }[] = eventTypesData?.response?.notificationEventTypes || []

    // eventType -> callback config id it is currently subscribed to
    const subscriptions: { [eventType: string]: string } = {}
    ;(profile?.subscriptions || []).forEach((subscription: any) => {
        (subscription.eventTypeSubscriptions || []).forEach((eventTypeSubscription: any) => {
            subscriptions[eventTypeSubscription.eventType] = eventTypeSubscription.callbackConfig?.id
        })
    })

    const isBusy = isCreating || isUpdating || isDeleting || isRefreshingSecret || isSubscribing || isUnsubscribing

    const runAction = async (action: () => Promise<any>, successMessage: string, failureMessage: string) => {
        try {
            const response = await action()
            dispatch(showSuccess(successMessage))
            return response
        } catch (err: any) {
            dispatch(showError(err?.data?.error || failureMessage))
        }
    }

    const onRefreshProfile = () => runAction(
        () => refreshProfile().unwrap(),
        'Notification profile refreshed',
        'Failed to refresh notification profile',
    )

    const onCreate = () => runAction(
        () => createCallbackConfig({ requestTimeoutSeconds: newTimeout }).unwrap(),
        'Callback config created',
        'Failed to create callback config',
    )

    const onStartEdit = (config: CallbackConfigType) => {
        setEditingConfigId(config.id)
        setEditForm({
            callbackUrl: config.callbackUrl || '',
            contactEmail: config.contactEmail || '',
            requestTimeoutSeconds: config.requestTimeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS,
        })
    }

    const onSaveEdit = async () => {
        if (!editingConfigId) return
        const response = await runAction(
            () => updateCallbackConfig({
                callbackConfigId: editingConfigId,
                callbackUrl: editForm.callbackUrl,
                contactEmail: editForm.contactEmail,
                requestTimeoutSeconds: Number(editForm.requestTimeoutSeconds),
            }).unwrap(),
            'Callback config updated',
            'Failed to update callback config',
        )
        if (response) setEditingConfigId(null)
    }

    const onConfirmDelete = async () => {
        if (!deleteModalConfigId) return
        await runAction(
            () => deleteCallbackConfig(deleteModalConfigId).unwrap(),
            'Callback config deleted',
            'Failed to delete callback config',
        )
        setDeleteModalConfigId(null)
    }

    const onConfirmRefreshSecret = async () => {
        if (!secretModalConfigId) return
        await runAction(
            () => refreshSecret({ callbackConfigId: secretModalConfigId, keepExistingSecretActive }).unwrap(),
            'Webhook secret updated',
            'Failed to update webhook secret',
        )
        setSecretModalConfigId(null)
    }

    const onToggleSubscription = (eventType: string) => {
        if (subscriptions[eventType]) {
            return runAction(
                () => unsubscribeEventType(eventType).unwrap(),
                `Unsubscribed from ${eventType}`,
                `Failed to unsubscribe from ${eventType}`,
            )
        }
        return runAction(
            () => subscribeEventType({ eventType, callbackConfigId: callbackConfigs[0]?.id }).unwrap(),
            `Subscribed to ${eventType}`,
            `Failed to subscribe to ${eventType}`,
        )
    }

    const onSendTest = async () => {
        let payload: any
        try {
            payload = JSON.parse(testPayload || '{}')
        } catch (err) {
            dispatch(showError('Test payload must be valid JSON'))
            return
        }
        const response = await runAction(
            () => sendTestNotification({ eventType: testEventType, payload }).unwrap(),
            'Test notification sent',
            'Failed to send test notification',
        )
        if (response) setTestResult(response.response)
    }

    if (isLoadingProfile) {
        return <LoadingSpinner text="Loading notification profile..." />
    }

    return (
        <div>
            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Card.Title className="mb-0">Callback Config</Card.Title>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={onRefreshProfile}
                            disabled={isRefreshingProfile || isFetchingProfile}
                        >
                            {isRefreshingProfile ? 'Refreshing...' : 'Refresh from VRBO'}
                        </Button>
                    </div>
                    <p className="text-muted">
                        The profile is cached on the server. Use Refresh to pull the latest from VRBO.
                    </p>

                    {!!profileError && (
                        <Alert variant="danger">Failed to load the notification profile.</Alert>
                    )}

                    {callbackConfigs.length === 0 && !profileError && (
                        <Row className="align-items-end g-2">
                            <Col xs="auto">
                                <Form.Group>
                                    <Form.Label>Request timeout (seconds)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        value={newTimeout}
                                        onChange={(e) => setNewTimeout(Number(e.target.value))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <Button onClick={onCreate} disabled={isBusy}>
                                    Create Callback Config
                                </Button>
                            </Col>
                        </Row>
                    )}

                    {callbackConfigs.map(config => (
                        <Card key={config.id} className="mb-3">
                            <Card.Body>
                                {editingConfigId === config.id ? (
                                    <Form>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Callback URL</Form.Label>
                                            <Form.Control
                                                value={editForm.callbackUrl}
                                                onChange={(e) => setEditForm({ ...editForm, callbackUrl: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Contact email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={editForm.contactEmail}
                                                onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Request timeout (seconds)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                value={editForm.requestTimeoutSeconds}
                                                onChange={(e) => setEditForm({ ...editForm, requestTimeoutSeconds: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Button className="me-2" onClick={onSaveEdit} disabled={isBusy}>Save</Button>
                                        <Button variant="secondary" onClick={() => setEditingConfigId(null)} disabled={isBusy}>Cancel</Button>
                                    </Form>
                                ) : (
                                    <>
                                        <Table size="sm" borderless className="mb-3">
                                            <tbody>
                                                <tr><th>ID</th><td>{config.id}</td></tr>
                                                <tr><th>Callback URL</th><td>{config.callbackUrl}</td></tr>
                                                <tr><th>Contact email</th><td>{config.contactEmail}</td></tr>
                                                <tr><th>Request timeout</th><td>{config.requestTimeoutSeconds ?? '-'} seconds</td></tr>
                                                <tr><th>Secret expires</th><td>{formatDateTime(config.secretExpirationDateTime)}</td></tr>
                                                {config.previousSecretExpirationDateTime && (
                                                    <tr><th>Previous secret expires</th><td>{formatDateTime(config.previousSecretExpirationDateTime)}</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <Button size="sm" onClick={() => onStartEdit(config)} disabled={isBusy}>Edit</Button>
                                            <Button size="sm" variant="warning" onClick={() => setSecretModalConfigId(config.id)} disabled={isBusy}>
                                                Update Secret
                                            </Button>
                                            <Form.Check
                                                type="checkbox"
                                                id={`keep-secret-${config.id}`}
                                                label="Keep previous secret active (up to 3 days)"
                                                checked={keepExistingSecretActive}
                                                onChange={(e) => setKeepExistingSecretActive(e.target.checked)}
                                            />
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                className="ms-auto"
                                                onClick={() => setDeleteModalConfigId(config.id)}
                                                disabled={isBusy}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    ))}
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Notification Event Types</Card.Title>
                    {isLoadingEventTypes && <LoadingSpinner text="Loading event types..." />}
                    {!!eventTypesError && <Alert variant="danger">Failed to load notification event types.</Alert>}
                    {callbackConfigs.length === 0 && (
                        <Alert variant="info">Create a callback config before subscribing to event types.</Alert>
                    )}
                    <Table responsive hover size="sm">
                        <thead>
                            <tr>
                                <th>Event type</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {eventTypes.map(eventType => {
                                const isSubscribed = !!subscriptions[eventType.name]
                                return (
                                    <tr key={eventType.name}>
                                        <td>{eventType.name}</td>
                                        <td>{eventType.description}</td>
                                        <td>
                                            <Badge bg={isSubscribed ? 'success' : 'secondary'}>
                                                {isSubscribed ? 'Subscribed' : 'Not subscribed'}
                                            </Badge>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                size="sm"
                                                variant={isSubscribed ? 'outline-danger' : 'outline-primary'}
                                                onClick={() => onToggleSubscription(eventType.name)}
                                                disabled={isBusy || (!isSubscribed && callbackConfigs.length === 0)}
                                            >
                                                {isSubscribed ? 'Unsubscribe' : 'Subscribe'}
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Send Test Notification</Card.Title>
                    <Form.Group className="mb-2">
                        <Form.Label>Event type</Form.Label>
                        <Form.Select value={testEventType} onChange={(e) => setTestEventType(e.target.value)}>
                            <option value="">Select an event type</option>
                            {eventTypes.map(eventType => (
                                <option key={eventType.name} value={eventType.name}>{eventType.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Payload (JSON)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            value={testPayload}
                            onChange={(e) => setTestPayload(e.target.value)}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Group>
                    <Button onClick={onSendTest} disabled={!testEventType || isSendingTest}>
                        {isSendingTest ? 'Sending...' : 'Send Test Notification'}
                    </Button>
                    {testResult && (
                        <div className="mt-3">
                            <JSONData title="Test result" data={testResult} onClose={() => setTestResult(null)} />
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Notification Profile</Card.Title>
                    <JSONData data={profile || { message: 'No notification profile found' }} />
                </Card.Body>
            </Card>

            <ConfirmModal
                title="Delete Callback Config"
                message="Are you sure you want to delete this callback config? VRBO will stop sending webhooks to this app, and the stored secret will be removed."
                active={!!deleteModalConfigId}
                onClose={() => setDeleteModalConfigId(null)}
                onConfirm={onConfirmDelete}
                disabled={isDeleting}
            />

            <ConfirmModal
                title="Update Webhook Secret"
                message={keepExistingSecretActive
                    ? 'Generate a new webhook secret? The previous secret stays valid for up to 3 days.'
                    : 'Generate a new webhook secret? The previous secret stops working immediately.'}
                active={!!secretModalConfigId}
                onClose={() => setSecretModalConfigId(null)}
                onConfirm={onConfirmRefreshSecret}
                disabled={isRefreshingSecret}
            />
        </div>
    )
}
